// src/contexts/AttorneyContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
} from "react";
import { Attorney, CaseAttorney } from "@visitproof/types";
import AttorneyService from "../services/AttorneyService";
import { useAccount } from "./AccountContext";
import { useAuth } from "./AuthContext";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}

interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface AttorneyContextProps {
  attorneys: Attorney[];
  selectedAttorney: Attorney | null;
  loading: Loadings;
  error: Errors;
  getAttorneyById: (id: string) => Attorney | undefined;
  createAttorney: (
    attorneyData: Omit<Attorney, "id" | "createdAt" | "updatedAt" | "accountId">,
  ) => Promise<string>;
  updateAttorney: (
    attorneyId: string,
    updatedData: Partial<Omit<Attorney, "id" | "createdAt" | "updatedAt">>,
  ) => Promise<void>;
  deleteAttorney: (attorneyId: string) => Promise<void>;
  setSelectedAttorney: (attorneyId: string) => void;
  clearSelectedAttorney: () => void;
  // Case-Attorney assignments
  assignAttorneyToCase: (
    assignmentData: Omit<CaseAttorney, "id" | "createdAt" | "updatedAt" | "accountId">,
  ) => Promise<string>;
  updateCaseAttorney: (
    assignmentId: string,
    updatedData: Partial<Omit<CaseAttorney, "id" | "createdAt" | "updatedAt" | "accountId" | "caseId" | "attorneyId">>,
  ) => Promise<void>;
  removeAttorneyFromCase: (assignmentId: string) => Promise<void>;
  getCaseAttorneys: (caseId: string) => Promise<CaseAttorney[]>;
  getAttorneyCases: (attorneyId: string) => Promise<CaseAttorney[]>;
}

const AttorneyContext = createContext<AttorneyContextProps | undefined>(
  undefined,
);

export const useAttorney = (): AttorneyContextProps => {
  const context = useContext(AttorneyContext);
  if (!context) {
    throw new Error("useAttorney must be used within an AttorneyProvider");
  }
  return context;
};

export const AttorneyProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { user, claims } = useAuth();
  const { account } = useAccount();
  const [attorneys, setAttorneys] = useState<Attorney[]>([]);
  const [selectedAttorney, setSelectedAttorneyState] = useState<Attorney | null>(null);
  const [loading, setLoading] = useState<Loadings>({
    list: true,
    details: false,
  });
  const [error, setError] = useState<Errors>({});

  // Use accountId from claims (works for all user types) or fall back to account.id
  // Note: claims has accountIds (array), so we take the first one
  const accountIds = claims?.accountIds as string[] | undefined;
  const accountId = accountIds?.[0] || account?.id;

  // Subscribe to attorneys list
  useEffect(() => {
    if (!accountId) {
      setLoading((prev) => ({ ...prev, list: false }));
      return;
    }

    setError((prev) => ({ ...prev, list: null }));

    const attorneysQuery = query(
      collection(db, "attorneys"),
      where("accountId", "==", accountId),
      orderBy("lastName", "asc"),
    );

    const unsubscribe = onSnapshot(
      attorneysQuery,
      (snapshot) => {
        const attorneysList: Attorney[] = [];
        snapshot.forEach((doc) => {
          attorneysList.push({
            id: doc.id,
            ...doc.data(),
          } as Attorney);
        });
        setAttorneys(attorneysList);
        setLoading((prev) => ({ ...prev, list: false }));
      },
      (err) => {
        console.error("Error fetching attorneys: ", err);
        setError((prev) => ({ ...prev, list: err }));
        setLoading((prev) => ({ ...prev, list: false }));
      },
    );

    return () => unsubscribe();
  }, [accountId]);

  // Update selected attorney when attorneys list changes
  useEffect(() => {
    if (selectedAttorney) {
      const updated = attorneys.find((a) => a.id === selectedAttorney.id);
      if (updated) {
        setSelectedAttorneyState(updated);
      }
    }
  }, [attorneys, selectedAttorney?.id]);

  const getAttorneyById = useCallback(
    (id: string): Attorney | undefined => {
      return attorneys.find((a) => a.id === id);
    },
    [attorneys],
  );

  const createAttorney = async (
    attorneyData: Omit<Attorney, "id" | "createdAt" | "updatedAt" | "accountId">,
  ): Promise<string> => {
    if (!accountId) {
      throw new Error("Account not available");
    }
    return AttorneyService.createAttorney({
      ...attorneyData,
      accountId,
    });
  };

  const updateAttorney = async (
    attorneyId: string,
    updatedData: Partial<Omit<Attorney, "id" | "createdAt" | "updatedAt">>,
  ): Promise<void> => {
    await AttorneyService.updateAttorney(attorneyId, updatedData);
  };

  const deleteAttorney = async (attorneyId: string): Promise<void> => {
    await AttorneyService.deleteAttorney(attorneyId);
    if (selectedAttorney?.id === attorneyId) {
      setSelectedAttorneyState(null);
    }
  };

  const setSelectedAttorney = async (attorneyId: string): Promise<void> => {
    setLoading((prev) => ({ ...prev, details: true }));
    try {
      const attorney = await AttorneyService.getAttorney(attorneyId);
      setSelectedAttorneyState(attorney);
      setError((prev) => ({ ...prev, details: null }));
    } catch (err) {
      setError((prev) => ({ ...prev, details: err as Error }));
    } finally {
      setLoading((prev) => ({ ...prev, details: false }));
    }
  };

  const clearSelectedAttorney = (): void => {
    setSelectedAttorneyState(null);
  };

  // Case-Attorney assignment methods
  const assignAttorneyToCase = async (
    assignmentData: Omit<CaseAttorney, "id" | "createdAt" | "updatedAt" | "accountId">,
  ): Promise<string> => {
    if (!accountId) {
      throw new Error("Account not available");
    }
    return AttorneyService.assignAttorneyToCase({
      ...assignmentData,
      accountId,
    });
  };

  const updateCaseAttorney = async (
    assignmentId: string,
    updatedData: Partial<Omit<CaseAttorney, "id" | "createdAt" | "updatedAt" | "accountId" | "caseId" | "attorneyId">>,
  ): Promise<void> => {
    await AttorneyService.updateCaseAttorney(assignmentId, updatedData);
  };

  const removeAttorneyFromCase = async (assignmentId: string): Promise<void> => {
    await AttorneyService.removeAttorneyFromCase(assignmentId);
  };

  const getCaseAttorneys = async (caseId: string): Promise<CaseAttorney[]> => {
    return AttorneyService.getCaseAttorneys(caseId);
  };

  const getAttorneyCases = async (attorneyId: string): Promise<CaseAttorney[]> => {
    if (!accountId) {
      return [];
    }
    return AttorneyService.getAttorneyCases(accountId, attorneyId);
  };

  return (
    <AttorneyContext.Provider
      value={{
        attorneys,
        selectedAttorney,
        loading,
        error,
        getAttorneyById,
        createAttorney,
        updateAttorney,
        deleteAttorney,
        setSelectedAttorney,
        clearSelectedAttorney,
        assignAttorneyToCase,
        updateCaseAttorney,
        removeAttorneyFromCase,
        getCaseAttorneys,
        getAttorneyCases,
      }}
    >
      {children}
    </AttorneyContext.Provider>
  );
};
